import React, { useState, useEffect } from 'react'
import { Layout, Card, Row, Col, Button, Typography, theme, Space, Select, notification, Modal, Avatar } from 'antd'
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UserOutlined
} from '@ant-design/icons'
import { getDoctorAvailability } from '../../api/searchApi'
import AppointmentConfirmationMobile from './AppointmentConfirmationMobile'

const { Header, Content } = Layout
const { Title, Text } = Typography
const { Option } = Select

const DoctorAvailabilityViewMobile = ({
  doctor,
  onClose,
  onBookAppointment,
  isVirtualVisit = false,
  searchFilters = {}
}) => {
  const { token } = theme.useToken()
  const [loading, setLoading] = useState(false)
  const [selectedReason, setSelectedReason] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date())
  const [availabilityData, setAvailabilityData] = useState(null)
  const [currentView, setCurrentView] = useState('calendar') // 'calendar' or 'confirmation'
  const [timeSlots, setTimeSlots] = useState([])
  const [selectedDay, setSelectedDay] = useState(null)
  const [showSlotModal, setShowSlotModal] = useState(false)
  const [pendingSlot, setPendingSlot] = useState(null)

  useEffect(() => {
    fetchAvailability()
  }, [doctor, currentWeekStart])

  const fetchAvailability = async () => {
    if (!doctor) return

    setLoading(true)
    try {
      const filter = {
        medicalProfessional: doctor._id,
        clinicRef: doctor.clinicRecord?._id
      }

      const availability = await getDoctorAvailability(filter)
      setAvailabilityData(availability)
      setTimeSlots(generateTimeSlots(availability))
    } catch (error) {
      console.error('Error fetching availability:', error)
    } finally {
      setLoading(false)
    }
  }

  const getMonday = (date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    return new Date(d.setDate(diff))
  }

  const getWeekDates = (startDate) => {
    const dates = []
    const monday = getMonday(startDate)

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday)
      date.setDate(monday.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  const formatDate = (date) => {
    const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
    const day = dayNames[date.getDay()]
    const dayNum = date.getDate()
    const month = date.getMonth() + 1
    return { day, dayNum, month, fullDate: `${day} ${dayNum}/${month}` }
  }

  const generateTimeSlots = (availability) => {
    const allTimes = new Set()
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]

    if (availability?.availability) {
      const filteredAvailability = availability.availability.filter((dayAvailability) => {
        return dayAvailability.date >= todayStr
      })

      filteredAvailability.forEach((dayAvailability) => {
        dayAvailability.timeSlots.forEach((slot) => {
          allTimes.add(slot.time)
        })
      })
    }

    const times = Array.from(allTimes).sort()
    if (times.length === 0) {
      const defaultTimes = [
        '08:00',
        '08:30',
        '09:00',
        '09:30',
        '10:00',
        '10:30',
        '11:00',
        '11:30',
        '12:00',
        '12:30',
        '13:00',
        '13:30',
        '14:00',
        '14:30',
        '15:00',
        '15:30',
        '16:00',
        '16:30',
        '17:00'
      ]
      times.push(...defaultTimes)
    }

    const weekDates = getWeekDates(currentWeekStart)

    return times.map((time) => ({
      time,
      slots: weekDates.map((date, dayIndex) => {
        const dateStr = date.toISOString().split('T')[0]

        if (dateStr < todayStr) {
          return {
            date,
            dayIndex,
            time,
            available: false,
            id: `${dayIndex}-${time}`,
            slotId: null
          }
        }

        const dayAvailability = availability?.availability?.find((avail) => avail.date === dateStr)
        const timeSlot = dayAvailability?.timeSlots?.find((slot) => slot.time === time)

        // If we have availability data, check if slot is not blocked
        // If we don't have availability data (using default times), assume available
        const isAvailable = availability?.availability?.length > 0 ? (timeSlot ? !timeSlot.blocked : false) : true

        return {
          date,
          dayIndex,
          time,
          available: isAvailable,
          id: `${dayIndex}-${time}`,
          slotId: timeSlot?._id
        }
      })
    }))
  }

  const handleSlotClick = (slot) => {
    if (!selectedReason) {
      notification.warning({
        message: 'Select Appointment Reason',
        description: 'Please select a reason for your visit before choosing a time slot.',
        duration: 3
      })
      return
    }

    // Set the pending slot and show modal
    setPendingSlot(slot)
    setShowSlotModal(true)
  }

  const handleConfirmSlot = () => {
    // Set the selected slot to show visual feedback
    setSelectedSlot(pendingSlot)
    setShowSlotModal(false)

    // Add a small delay before transitioning to confirmation for better UX
    setTimeout(() => {
      setCurrentView('confirmation')
    }, 200)
  }

  const handleCancelSlot = () => {
    setPendingSlot(null)
    setShowSlotModal(false)
  }

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart)
    newDate.setDate(newDate.getDate() + direction * 7)

    if (direction < 0) {
      const today = new Date()
      const currentWeekMonday = getMonday(today)
      if (newDate < currentWeekMonday) return
    }

    setCurrentWeekStart(newDate)
  }

  const appointmentReasons = [
    'General Consultation',
    'Follow-up Appointment',
    'Medical Certificate',
    'Prescription Renewal',
    'Mental Health Consultation',
    'Assessment',
    'Other'
  ]

  const handleConfirmBooking = async (bookingData) => {
    try {
      await onBookAppointment?.(bookingData)
    } catch (error) {
      console.error('Booking error:', error)
    }
  }

  // Get time slots for a specific day
  const getTimeSlotsForDay = (dayIndex) => {
    const slots = []
    timeSlots.forEach((timeSlot) => {
      const slot = timeSlot.slots[dayIndex]
      if (slot) {
        slots.push(slot)
      }
    })
    return slots
  }

  // Auto-select today if available, otherwise first available day
  useEffect(() => {
    if (timeSlots.length > 0 && selectedDay === null) {
      const today = new Date()
      const weekDates = getWeekDates(currentWeekStart)

      // Try to find today in the current week
      const todayIndex = weekDates.findIndex((date) => date.toDateString() === today.toDateString())

      if (todayIndex !== -1 && todayIndex < weekDates.length) {
        // Check if today has available slots
        const todaySlots = getTimeSlotsForDay(todayIndex)
        if (todaySlots.some((slot) => slot.available)) {
          setSelectedDay(todayIndex)
          return
        }
      }

      // Find first day with available slots
      for (let i = 0; i < weekDates.length; i++) {
        const daySlots = getTimeSlotsForDay(i)
        if (daySlots.some((slot) => slot.available)) {
          setSelectedDay(i)
          break
        }
      }
    }
  }, [timeSlots, currentWeekStart])

  // Reset selected day when week changes
  useEffect(() => {
    setSelectedDay(null)
  }, [currentWeekStart])

  if (currentView === 'confirmation' && selectedSlot) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
        <Header
          style={{
            background: '#fff',
            padding: '0 16px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}
        >
          <Button
            type='text'
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              console.log('Here at confirm')
              setCurrentView('calendar')
            }}
            style={{ marginRight: '12px' }}
          />
          <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
            Confirm Booking
          </Title>
        </Header>

        <Content style={{ padding: 0 }}>
          <div className='mobile-availability-container'>
            <AppointmentConfirmationMobile
              doctor={doctor}
              selectedSlot={selectedSlot}
              selectedReason={selectedReason}
              onConfirm={handleConfirmBooking}
              onBack={() => setCurrentView('calendar')}
              isVirtualVisit={isVirtualVisit}
              searchFilters={searchFilters}
            />
          </div>
        </Content>
      </Layout>
    )
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Header
        style={{
          background: '#fff',
          padding: '0 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <Button type='text' icon={<ArrowLeftOutlined />} onClick={onClose} style={{ marginRight: '12px' }} />
        <Title
          level={5}
          style={{
            margin: 0,
            fontSize: '16px',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          Book with {doctor?.firstName} {doctor?.lastName}
        </Title>
      </Header>

      <Content style={{ padding: '16px', overflow: 'auto' }} className='mobile-availability-container'>
        {/* Doctor Info Card */}
        <Card style={{ marginBottom: '16px', borderRadius: '12px' }}>
          <Row align='middle' gutter={16}>
            <Col>
              <Avatar size={64} src={doctor.image} icon={<UserOutlined />} />
            </Col>
            <Col flex={1}>
              <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
                {doctor?.firstName} {doctor?.lastName}
              </Title>
              <Text type='secondary' style={{ fontSize: '14px' }}>
                {doctor.extraData?.profession}
              </Text>
              <br />
              <Text style={{ fontSize: '12px', color: token.colorPrimary }}>{doctor.clinicRecord?.name}</Text>
            </Col>
          </Row>
        </Card>

        {/* Appointment Reason */}
        <Card style={{ marginBottom: '16px', borderRadius: '12px' }}>
          <Title level={5} style={{ marginBottom: '16px', fontSize: '16px' }}>
            <CalendarOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
            Select Appointment Reason
          </Title>
          <Select
            placeholder='Choose the reason for your visit...'
            value={selectedReason}
            onChange={setSelectedReason}
            style={{ width: '100%' }}
            size='large'
          >
            {appointmentReasons.map((reason) => (
              <Option key={reason} value={reason}>
                {reason}
              </Option>
            ))}
          </Select>
        </Card>

        {/* Calendar */}
        <Card style={{ borderRadius: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
              <ClockCircleOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Available Times
            </Title>
            <Space>
              <Button
                type='text'
                onClick={() => navigateWeek(-1)}
                disabled={currentWeekStart <= getMonday(new Date())}
                style={{ fontSize: '18px', height: '32px', width: '32px' }}
              >
                ←
              </Button>
              <Button
                type='text'
                onClick={() => navigateWeek(1)}
                style={{ fontSize: '18px', height: '32px', width: '32px' }}
              >
                →
              </Button>
            </Space>
          </div>

          {/* Selectable Days */}
          <div style={{ marginBottom: '16px' }}>
            <Row gutter={[8, 8]}>
              {getWeekDates(currentWeekStart).map((date, index) => {
                const formatted = formatDate(date)
                const today = new Date()
                const isToday = date.toDateString() === today.toDateString()
                const isSelected = selectedDay === index
                const isPast = date < new Date(new Date().setHours(0, 0, 0, 0))

                return (
                  <Col key={index} span={8} style={{ marginBottom: '8px' }}>
                    <Button
                      block
                      type={isSelected ? 'primary' : 'default'}
                      disabled={isPast}
                      onClick={() => setSelectedDay(index)}
                      style={{
                        height: '56px',
                        borderRadius: '8px',
                        backgroundColor: isSelected
                          ? token.colorPrimary
                          : isToday && !isSelected
                          ? '#e6f7ff'
                          : isPast
                          ? '#f5f5f5'
                          : '#fff',
                        borderColor: isSelected
                          ? token.colorPrimary
                          : isToday && !isSelected
                          ? token.colorPrimary
                          : '#d9d9d9',
                        color: isSelected ? '#fff' : isPast ? '#bbb' : '#000',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '12px'
                      }}
                    >
                      <div>{formatted.day}</div>
                      <div style={{ fontSize: '14px', marginTop: '2px' }}>
                        {formatted.dayNum}/{formatted.month}
                      </div>
                    </Button>
                  </Col>
                )
              })}
            </Row>
          </div>

          {/* Time Slots for Selected Day */}
          {selectedDay !== null && (
            <div>
              <Title level={5} style={{ fontSize: '14px', marginBottom: '12px', color: '#666' }}>
                {formatDate(getWeekDates(currentWeekStart)[selectedDay]).fullDate}
              </Title>

              <Row gutter={[8, 8]}>
                {getTimeSlotsForDay(selectedDay).map((slot) => {
                  const isSelected = selectedSlot?.id === slot.id
                  const isPending = pendingSlot?.id === slot.id
                  const canSelect = slot.available && selectedReason

                  return (
                    <Col key={slot.id} span={12} style={{ marginBottom: '8px' }}>
                      <Button
                        block
                        type={isSelected || isPending ? 'primary' : 'default'}
                        disabled={!canSelect}
                        onClick={() => canSelect && handleSlotClick(slot)}
                        className={`mobile-time-slot-button ${isSelected || isPending ? 'selected' : ''}`}
                        style={{
                          height: '48px',
                          fontSize: '16px',
                          fontWeight: '600',
                          borderRadius: '8px',
                          backgroundColor:
                            isSelected || isPending ? token.colorPrimary : slot.available ? '#fff' : '#f5f5f5',
                          borderColor:
                            isSelected || isPending ? token.colorPrimary : slot.available ? '#d9d9d9' : '#f5f5f5',
                          color: isSelected || isPending ? '#fff' : slot.available ? '#000' : '#bbb'
                        }}
                      >
                        {slot.time}
                      </Button>
                    </Col>
                  )
                })}
              </Row>

              {getTimeSlotsForDay(selectedDay).length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '24px 16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    border: '1px solid #e5e5e5'
                  }}
                >
                  <Text style={{ color: '#666', fontSize: '14px' }}>No available time slots for this day</Text>
                </div>
              )}
            </div>
          )}

          {selectedDay === null && (
            <div
              style={{
                textAlign: 'center',
                padding: '24px 16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}
            >
              <Text style={{ color: token.colorPrimary, fontSize: '14px' }}>
                Please select a day above to view available times
              </Text>
            </div>
          )}

          {!selectedReason && (
            <div
              style={{
                textAlign: 'center',
                padding: '16px',
                backgroundColor: '#fef3cd',
                borderRadius: '8px',
                marginTop: '16px',
                border: '1px solid #ffeaa7'
              }}
            >
              <Text style={{ color: '#856404', fontSize: '14px' }}>
                Please select an appointment reason above to view available times
              </Text>
            </div>
          )}
        </Card>

        {/* Slot Confirmation Modal */}
        <Modal
          open={showSlotModal}
          onCancel={handleCancelSlot}
          onOk={handleConfirmSlot}
          centered
          title={null}
          footer={null}
          width={320}
          closable={false}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CalendarOutlined
              style={{
                fontSize: '48px',
                color: token.colorPrimary,
                marginBottom: '16px',
                display: 'block'
              }}
            />
            <Title level={4} style={{ marginBottom: '8px' }}>
              Confirm Time Slot
            </Title>
            <Text style={{ fontSize: '16px', display: 'block', marginBottom: '16px' }}>
              Are you sure you want to book this appointment?
            </Text>
            {pendingSlot && (
              <div
                style={{
                  background: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>Date: </Text>
                  <Text>{formatDate(pendingSlot.date).fullDate}</Text>
                </div>
                <div>
                  <Text strong>Time: </Text>
                  <Text>{pendingSlot.time}</Text>
                </div>
              </div>
            )}
            <Space size={12} style={{ width: '100%', justifyContent: 'center' }}>
              <Button size='large' onClick={handleCancelSlot} style={{ minWidth: '100px' }}>
                Cancel
              </Button>
              <Button
                type='primary'
                size='large'
                onClick={handleConfirmSlot}
                style={{
                  minWidth: '100px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                  border: 'none'
                }}
              >
                Confirm
              </Button>
            </Space>
          </div>
        </Modal>
      </Content>
    </Layout>
  )
}

export default DoctorAvailabilityViewMobile
